import React, { ChangeEvent, DragEvent, useRef, useState } from 'react';

import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Alert, Box, Button, Checkbox, FormControlLabel, Grid, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import type { ClaimCampaign, ClaimCsvRow } from '~/types/loyaltyContract';

import { downloadClaimsTemplate, parseClaimsCsv, validateClaimsCsvRows } from '../csvHelpers';

interface Props {
  campaign: ClaimCampaign;
  onBack: () => void;
  onProceedToReview: (rows: Array<ClaimCsvRow>, options?: { enrolMember?: boolean }) => void;
}

const BASE_COLUMNS: Array<GridColDef<ClaimCsvRow>> = [{ field: 'customerId', headerName: 'CUSTOMER ID', flex: 1 }];

const CODE_COLUMN: GridColDef<ClaimCsvRow> = {
  field: 'code',
  headerName: 'CODE',
  flex: 1,
};

export default function BulkUploadStep({ campaign, onBack, onProceedToReview }: Props) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [parsedRows, setParsedRows] = useState<Array<ClaimCsvRow>>([]);
  const [parseErrors, setParseErrors] = useState<Array<string>>([]);
  const [fileName, setFileName] = useState<string>('');
  const [enrolMember, setEnrolMember] = useState(false);

  const [isDragOver, setIsDragOver] = useState(false);

  const requiresCode = campaign.validatorType === 'PREGEN_CODE' || campaign.validatorType === 'SHARED_CODE';
  const columns = requiresCode ? [...BASE_COLUMNS, CODE_COLUMN] : BASE_COLUMNS;

  const processFile = (file: File) => {
    setParseErrors([]);
    setParsedRows([]);
    setFileName(file.name);

    if (file.size > 25 * 1024 * 1024) {
      setParseErrors(['File exceeds maximum size of 25MB']);
      return;
    }

    const reader = new FileReader();
    reader.onload = (evt) => {
      if (!evt?.target?.result || typeof evt.target.result !== 'string') {
        return;
      }

      const { data, parseErrors: csvParseErrors } = parseClaimsCsv(evt.target.result);

      if (csvParseErrors.length > 0) {
        setParseErrors(csvParseErrors);
        return;
      }

      const { rows, errors } = validateClaimsCsvRows(data);

      if (requiresCode) {
        const missingCodeRows = rows.map((row, i) => (!row.code ? i + 2 : null)).filter((v): v is number => v !== null);
        if (missingCodeRows.length > 0) {
          errors.push(
            `Rows ${missingCodeRows.slice(0, 5).join(', ')}${
              missingCodeRows.length > 5 ? '...' : ''
            }: missing code (required for this campaign)`,
          );
        }
      }

      if (errors.length > 0) {
        setParseErrors(errors);
        return;
      }

      setParsedRows(rows);
    };
    reader.readAsText(file);
  };

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    // Reset input so re-uploading the same file triggers onChange
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    processFile(file);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file || !file.name.endsWith('.csv')) {
      setParseErrors(['Please upload a CSV file']);
      return;
    }
    processFile(file);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const canProceed = parsedRows.length > 0;

  return (
    <Box>
      <Grid container spacing={3} columns={12}>
        {/* Left column - Upload */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={3}>
            {/* Upload zone */}
            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              sx={{
                border: 2,
                borderStyle: 'dashed',
                borderColor: isDragOver ? 'primary.main' : 'divider',
                bgcolor: isDragOver ? 'action.hover' : 'transparent',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                transition: 'all 0.15s ease',
              }}
            >
              <UploadFileIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="subtitle1" gutterBottom>
                Upload New File
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Drag and drop your customer list here or click to browse files
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                Supported: CSV (Max 25MB)
              </Typography>
              <Button variant="contained" component="label">
                Browse Files
                <input ref={fileInputRef} type="file" hidden accept=".csv" onChange={handleFileUpload} />
              </Button>
            </Box>

            {/* Template download */}
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Need the template?
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={1}>
                CSV columns: <strong>customer_id</strong>
                {requiresCode && (
                  <>
                    , <strong>code</strong> (required for this campaign)
                  </>
                )}
              </Typography>
              <Button size="small" startIcon={<DownloadIcon />} onClick={downloadClaimsTemplate}>
                Download CSV Template
              </Button>
            </Box>

            {/* File statistics */}
            {parsedRows.length > 0 && (
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  File: {fileName}
                </Typography>
                <Typography variant="body2">
                  {parsedRows.length} row{parsedRows.length !== 1 ? 's' : ''} ready to process
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                bgcolor: enrolMember ? 'rgba(2, 136, 209, 0.08)' : 'action.hover',
                border: 1,
                borderColor: enrolMember ? 'info.main' : 'divider',
                borderRadius: 1,
                p: 2,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              onClick={() => setEnrolMember((prev) => !prev)}
            >
              <FormControlLabel
                control={<Checkbox checked={enrolMember} onChange={(e) => setEnrolMember(e.target.checked)} />}
                label={
                  <Box>
                    <Typography variant="subtitle2">Enrol customers into Société</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Customers not yet enrolled will be automatically added to the loyalty program
                    </Typography>
                  </Box>
                }
                sx={{ m: 0, alignItems: 'flex-start' }}
              />
            </Box>
          </Stack>
        </Grid>

        {/* Right column - Data Preview */}
        <Grid size={{ xs: 12, md: 8 }}>
          {parseErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                CSV Validation Errors:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {parseErrors.slice(0, 10).map((error, i) => (
                  <li key={i}>{error}</li>
                ))}
                {parseErrors.length > 10 && <li>...and {parseErrors.length - 10} more</li>}
              </ul>
            </Alert>
          )}

          {parsedRows.length > 0 && (
            <>
              <Typography variant="subtitle1" mb={1}>
                Data Preview{' '}
                <Typography component="span" variant="body2" color="text.secondary">
                  {parsedRows.length} ROWS
                </Typography>
              </Typography>
              <DataGrid
                rows={parsedRows}
                columns={columns}
                getRowId={(row) => row.customerId}
                initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
                pageSizeOptions={[25, 50, 100]}
                autoHeight
                disableRowSelectionOnClick
              />
            </>
          )}

          {parsedRows.length === 0 && parseErrors.length === 0 && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={300}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
            >
              <Typography color="text.secondary">Upload a CSV file to preview data</Typography>
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Bottom bar */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mt={3}
        pt={2}
        borderTop={1}
        borderColor="divider"
      >
        <Box>
          {parsedRows.length > 0 && (
            <Typography variant="body2" color="success.main">
              {parsedRows.length} row{parsedRows.length !== 1 ? 's' : ''} will be processed
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={2}>
          <Button variant="text" onClick={onBack}>
            Back
          </Button>
          <Button
            variant="contained"
            onClick={() => onProceedToReview(parsedRows, { enrolMember })}
            disabled={!canProceed}
          >
            Proceed to Review
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
